import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useGetAccountDetails } from '@/api/settings/useGetAccountDetails';
import { useUpdateAccountDetails } from '@/api/settings/useUpdateAccountDetails';
import { Button } from '@/component-library/primitives/Button/Button';
import SettingsLoader from '@/component-library/primitives/Loaders/SettingsLoader';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import { Input } from '@/component-library/widgets/Input/Input';
import { CompanyAccountDetails, EditCompanyAccountDetails } from '@/models/CompanyAccountDetails';
import { AppRoutes } from '@/utils/routes/router';

// Unified color palette
const COLORS = {
  primary: '#316bffff',
  secondary: '#7898FB',
  positive: '#529F7C',
  negative: '#E87171',
  warning: '#F5F069',
  background: {
    card: '#111827',
    cardHover: '#1f2937',
  },
  border: '#374151',
  text: {
    primary: '#ffffff',
    secondary: '#9ca3af',
    muted: '#6b7280',
  },
};

// Card styles for sections
const sectionStyles = {
  card: 'bg-primary-0 rounded-lg border border-primary-100 p-6',
  title: 'text-lg font-semibold text-custom-textBlack mb-4',
  subtitle: 'text-sm text-gray-400 mb-6',
  sectionHeader: 'text-sm font-medium mb-4',
  grid: 'grid grid-cols-1 md:grid-cols-2 gap-6 mt-8',
};

const CompanyAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isFetched, isLoading } = useGetAccountDetails();
  const updateAccountDetailsMutation = useUpdateAccountDetails();

  const [account, setAccount] = useState<EditCompanyAccountDetails | undefined>(undefined);
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [open, setOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });

  useEffect(() => {
    if (isFetched) {
      setAccount(data);
    }
  }, [data, isFetched]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (toastInfo.toastType === 'success') {
      timer = setTimeout(() => navigate(AppRoutes.account), 2000);
    }
    return () => clearTimeout(timer);
  }, [navigate, toastInfo]);

  const schema = useMemo(() => {
    return yup
      .object()
      .shape({
        tenantName: yup.string().trim().required(t('CommonErrors.companyNameRequired')),
        invoiceEmail: yup
          .string()
          .trim()
          .email(t('CommonErrors.emailFormatValidation'))
          .required(t('CommonErrors.invoiceEmailAddressRequired')),
        legalEmail: yup
          .string()
          .trim()
          .email(t('CommonErrors.emailFormatValidation'))
          .required(t('CommonErrors.legalEmailAddressRequired')),
        technicalEmail: yup
          .string()
          .trim()
          .email(t('CommonErrors.emailFormatValidation'))
          .required(t('CommonErrors.technicalEmailAddressRequired')),
        tenantAddress: yup.string().trim().required(t('CommonErrors.addressRequired')),
        city: yup.string().trim().required(t('CommonErrors.cityRequired')),
        zipCode: yup.string().trim().required(t('CommonErrors.zipOrPostalCodeIsRequired')),
      })
      .required();
  }, [t]);

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<CompanyAccountDetails>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (account && !isInEditMode) {
      setValue('tenantName', account.tenantName);
      setValue('invoiceEmail', account.invoiceEmail);
      setValue('legalEmail', account.legalEmail);
      setValue('technicalEmail', account.technicalEmail);
      setValue('tenantAddress', account.tenantAddress);
      setValue('tenantNumber', account.tenantNumber);
      setValue('city', account.city);
      setValue('zipCode', account.zipCode);
      clearErrors();
    }
  }, [account, setValue, isInEditMode, clearErrors]);

  const watchAll = watch();

  const onSubmit = (data: EditCompanyAccountDetails) => {
    const sendData = {
      tenantName: data.tenantName,
      invoiceEmail: data.invoiceEmail,
      legalEmail: data.legalEmail,
      technicalEmail: data.technicalEmail,
      createdAt: new Date().toISOString(),
      tenantAddress: data.tenantAddress,
      tenantNumber: '+381628145689',
      city: data.city,
      zipCode: data.zipCode,
    };
    updateAccountDetailsMutation.mutate(sendData as CompanyAccountDetails, {
      onSuccess: () => {
        setAccount(sendData);
        setToastInfo({ message: t('CompanyAccountSettingsPage.toastSuccess'), toastType: 'success' });
        setOpen(true);
        setIsInEditMode(false);
      },
      onError: () => {
        setToastInfo({ message: t('CompanyAccountSettingsPage.toastError'), toastType: 'error' });
        setOpen(true);
      },
    });
  };

  const onCancel = () => {
    isInEditMode ? setIsInEditMode(false) : navigate(AppRoutes.settings);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <SettingsLoader />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center max-w-[40rem] mx-auto">
      <h1 className="text-custom-black w-full font-medium text-xl mb-2">{t('CompanyAccountSettingsPage.title')}</h1>
      <p className="w-full text-sm text-custom-darkSilver mb-8">{t('CompanyAccountSettingsPage.description')}</p>

      <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-8">
        {/* Basic Information */}
        <section className="space-y-4">
          <h2 className="text-xs font-semibold text-custom-blue uppercase">
            {t('CompanyAccountSettingsPage.basicInformationLabel')}
          </h2>

          <Input
            readOnly={!isInEditMode}
            label={t('CompanyAccountSettingsPage.companyNameLabel')}
            placeholder={
              !isInEditMode && !watchAll.tenantName
                ? t('CompanyAccountSettingsPage.undefinedLabel')
                : t('CompanyAccountSettingsPage.companyNamePlaceholder')
            }
            {...register('tenantName')}
            // className={`
            //     ${!isInEditMode && '!pl-0 !pt-0 bg-transparent border-transparent'}
            //     ${errors.tenantName ? 'border-custom-red' : ''}
            //   `}
            error={errors.tenantName?.message as string}
          />
        </section>

        <hr className="w-full border-custom-lightSilver" />

        {/* Contact Information */}
        <section className="space-y-4">
          <h2 className="text-xs font-semibold text-custom-blue uppercase">
            {t('CompanyAccountSettingsPage.contactInformationLabel')}
          </h2>

          <Input
            readOnly={!isInEditMode}
            label={t('CompanyAccountSettingsPage.invoiceEmailAddressLabel')}
            placeholder={
              !isInEditMode && !watchAll.invoiceEmail
                ? t('CompanyAccountSettingsPage.undefinedLabel')
                : t('CompanyAccountSettingsPage.invoiceEmailAddressPlaceholder')
            }
            {...register('invoiceEmail')}
            error={errors.invoiceEmail?.message as string}
          />
          <Input
            readOnly={!isInEditMode}
            label={t('CompanyAccountSettingsPage.companyEmailAddressLabel')}
            placeholder={
              !isInEditMode && !watchAll.legalEmail
                ? t('CompanyAccountSettingsPage.undefinedLabel')
                : t('CompanyAccountSettingsPage.companyEmailAddressPlaceholder')
            }
            {...register('legalEmail')}
            error={errors.legalEmail?.message as string}
          />
          <Input
            readOnly={!isInEditMode}
            label={t('CompanyAccountSettingsPage.technicalContactEmailAddressLabel')}
            placeholder={
              !isInEditMode && !watchAll.technicalEmail
                ? t('CompanyAccountSettingsPage.undefinedLabel')
                : t('CompanyAccountSettingsPage.technicalContactEmailAddressPlaceholder')
            }
            {...register('technicalEmail')}
            error={errors.technicalEmail?.message as string}
          />
        </section>

        <hr className="w-full border-custom-lightSilver" />

        {/* Location Information */}
        <section className="space-y-4">
          <h2 className="text-xs font-semibold text-custom-blue uppercase">
            {t('CompanyAccountSettingsPage.locationInformationLabel')}
          </h2>

          <Input
            readOnly={!isInEditMode}
            label={t('CompanyAccountSettingsPage.addressLabel')}
            placeholder={
              !isInEditMode && !watchAll.tenantAddress
                ? t('CompanyAccountSettingsPage.undefinedLabel')
                : t('CompanyAccountSettingsPage.addressPlaceholder')
            }
            {...register('tenantAddress')}
            error={errors.tenantAddress?.message as string}
          />

          <Input
            readOnly={!isInEditMode}
            label={t('CompanyAccountSettingsPage.cityLabel')}
            placeholder={
              !isInEditMode && !watchAll.city
                ? t('CompanyAccountSettingsPage.undefinedLabel')
                : t('CompanyAccountSettingsPage.cityPlaceholder')
            }
            {...register('city')}
            error={errors.city?.message as string}
          />
          <Input
            readOnly={!isInEditMode}
            label={t('CompanyAccountSettingsPage.zipOrPostalCodeLabel')}
            placeholder={
              !isInEditMode && !watchAll.zipCode
                ? t('CompanyAccountSettingsPage.undefinedLabel')
                : t('CompanyAccountSettingsPage.zipOrPostalCodePlaceholder')
            }
            {...register('zipCode')}
            error={errors.zipCode?.message as string}
          />
        </section>

        <hr className="w-full border-custom-lightSilver" />

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="transparent" onClick={onCancel}>
            {isInEditMode ? t('CompanyAccountSettingsPage.linkCancel') : 'Back to Settings'}
          </Button>

          {isInEditMode ? (
            <Button
              type="submit"
              style={{ backgroundColor: COLORS.primary }}
              disabled={updateAccountDetailsMutation.isLoading}
              isLoading={updateAccountDetailsMutation.isLoading}
            >
              {t('CompanyAccountSettingsPage.buttonUpdateCompanyAccount')}
            </Button>
          ) : (
            <Button
              variant="transparent"
              onClick={(e) => {
                e.preventDefault();
                setIsInEditMode(true);
              }}
            >
              {t('CompanyAccountSettingsPage.buttonEditCompanyAccount')}
            </Button>
          )}
        </div>
      </form>

      <ProvideToasts>
        <Toast
          isOpen={open}
          setOpen={setOpen}
          message={toastInfo.message}
          toastType={toastInfo.toastType as ToastType}
        />
      </ProvideToasts>
    </div>
  );
};

export default CompanyAccount;
